import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useGetTransferDetailsQuery, useCreateTransferMutation, useUploadTransferImagesMutation } from '../slices/transfersApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const TransfersEditScreen = () => {
  const { id: transfersId } = useParams(); // Retrieve transfer ID
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [uploadTransferImages] = useUploadTransferImagesMutation();
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [images, setImages] = useState([]);
  const { data: transferData, isLoading: loadingTransfer, error: transferError } = useGetTransferDetailsQuery(transfersId);
  const { data: customersData, isLoading: loadingCustomers, error: customersError } = useGetCustomersQuery({ keyword: customerSearch });
  const [createTransfer, { isLoading: savingTransfer, }] = useCreateTransferMutation();

  // Load initial transfer data into the form
  useEffect(() => {
    if (transferData) {
      setSelectedCustomer(transferData.customerDetails || null);
      setNote(transferData.note || '');
      setImages(transferData.images || []);
      setTransferDate(transferData.date ? transferData.date.split('T')[0] : new Date().toISOString().split('T')[0]);
      dispatch(clearCartItems());
      transferData.transferItems.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product, // Ensure product ID is preserved
            qty: item.qty,
          })
        );
      });
    }
  }, [transferData, dispatch]);

  // Filter customers based on search input
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const increaseQtyHandler = (item) => {
    dispatch(
      addToCart({
        ...item,
        qty: item.qty + 1,
      })
    );
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(
        addToCart({
          ...item,
          qty: item.qty - 1,
        })
      );
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const placeTransferHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      // Construct transfersData payload for submission
      const transfersData = {
        transferItems: cartItems.map((item) => ({
          product: item.product, // Include product ID
          name: item.name,
          qty: item.qty,
          price: item.price,
          images: item.images[0], // Include first image
        })),
        customerDetails: selectedCustomer._id,
        note,
        date: transferDate,
        images,
      };

      // Submit as a new transfer record
      const response = await createTransfer(transfersData).unwrap();
      console.log('Response:', response); // Debug backend response
      dispatch(clearCartItems());
      toast.success('Transfer successfully recorded');
      navigate('/house/HomeScreen'); // Redirect after success
    } catch (err) {
      console.error('Error:', err); // Debug error
      toast.error(err?.data?.message || 'Failed to record transfers');
    }
  };

  const validateFiles = (files) => {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      for (let file of files) {
        if (!allowedTypes.includes(file.type)) {
          toast.error(`File type not allowed: ${file.type}`);
          return false;
        }
        if (file.size > maxSize) {
          toast.error(`File size exceeds 5MB: ${file.name}`);
          return false;
        }
      }
      return true;
    };
  
  const uploadImageHandler = async (files) => {
      if (!validateFiles(files)) return;
  
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('images', file));
  
      try {
        const res = await uploadTransferImages(formData).unwrap();
        toast.success(res.message);
        setImages([...images, ...res.images]);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    };
  
    const removeImageHandler = (index) => {
      setImages(images.filter((_, i) => i !== index));
    };
  

  return (
    <Row>
      <Col md={8}>
        <h1>Edit Transfer</h1>
        {loadingTransfer ? (
          <Loader />
        ) : transferError ? (
          <Message variant="danger">{transferError?.data?.message || 'Failed to load transfer data'}</Message>
        ) : (
          <>
            {cartItems.length === 0 ? (
              <Message>
                Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
              </Message>
            ) : (
              <ListGroup variant="flush">
                {cartItems.map((item) => (
                  <ListGroup.Item key={item._id}>
                    <Row>
                      <Col md={2}>
                        {item.images?.length > 0 && (
                          <img
                            src={item.images[0]}
                            alt="Product Thumbnail"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      </Col>
                      <Col>{item.name}</Col>
                      <Col md={2}>${item.price.toFixed(2)}</Col>
                      <Col md={3}>
                        <div className="d-flex align-items-center">
                          <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                            <FaMinus />
                          </Button>
                          <span className="mx-2">{item.qty}</span>
                          <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                            <FaPlus />
                          </Button>
                        </div>
                      </Col>
                      <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                      <Col md={1}>
                        <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Col>
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Customer</h2>
              {loadingCustomers ? (
                <Loader />
              ) : customersError ? (
                <Message variant="danger">{customersError?.data?.message || 'Failed to load customers'}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>
                  {filteredCustomers.length > 0 && (
                    <ListGroup>
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serial}
                      </p>
                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="transferDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={transferDate}
                          onChange={(e) => setTransferDate(e.target.value)}
                        />
                      </Form.Group>
                     <Form.Group controlId="images" className="mb-3">
                                                                   <Form.Label>Images</Form.Label>
                                                                   <Form.Control
                                                                     type="file"
                                                                     multiple
                                                                     onChange={(e) => uploadImageHandler(e.target.files)}
                                                                   />
                                                                   <div className="mt-3">
                                                                     {images.map((img, idx) => (
                                                                       <div
                                                                         key={idx}
                                                                         style={{
                                                                           display: 'inline-block',
                                                                           position: 'relative',
                                                                           marginRight: '10px',
                                                                         }}
                                                                       >
                                                                         <img
                                                                           src={img}
                                                                           alt={`Uploaded ${idx}`}
                                                                           className="img-fluid"
                                                                           style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                                                                         />
                                                                         <Button
                                                                           variant="danger"
                                                                           size="sm"
                                                                           onClick={() => removeImageHandler(idx)}
                                                                           style={{
                                                                             position: 'absolute',
                                                                             top: '0',
                                                                             right: '0',
                                                                             borderRadius: '50%',
                                                                             padding: '2px 5px',
                                                                           }}
                                                                         >
                                                                           ✖
                                                                         </Button>
                                                                       </div>
                                                                     ))}
                                  </div>
                       </Form.Group>
                                           



                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={savingTransfer || cartItems.length === 0 || !selectedCustomer}
                onClick={placeTransferHandler}
              >
                {savingTransfer ? 'Processing...' : 'Submit Transfer'}
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default TransfersEditScreen;